import clsx from 'clsx'
import { createFileRoute } from '@tanstack/react-router'
import { useForm, useStore } from '@tanstack/react-form'
import { useMutation, useQueryClient } from '@tanstack/react-query'

import Spinner from '@tandem/ui/Spinner'
import Errors from '@tandem/ui/Errors'
import { useTRPC } from '@/trpc'
import { useAuth } from '@/auth/AuthContext'
import { CompanyType, createCompany } from '@tandem/schema/company'

import SplashLayout from '@/layouts/Splash'
import TruckBoxFillIcon from '@tandem/ui/icons/truck.box.fill.svg?react'
import StorefrontFillIcon from '@tandem/ui/icons/storefront.fill.svg?react'

import $form from '@tandem/ui/form.css'
import $type from '@tandem/ui/type.css'
import $anim from '@tandem/ui/anim.css'
import $ from './-styles.css'

export const Route = createFileRoute('/onboarding/')({ component: OnboardingPage })

function OnboardingPage() {
  let trpc = useTRPC()
  let queryClient = useQueryClient()
  let { refetch } = useAuth()

  let setup = useMutation(
    trpc.company.setup.mutationOptions({
      onSuccess() {
        queryClient.invalidateQueries(trpc.auth.me.queryFilter())
        refetch()
        window.location.href = '/'
      }
    })
  )

  let form = useForm({
    defaultValues: {
      name: '',
      type: '' as CompanyType | '',
      storeName: ''
    },
    canSubmitWhenInvalid: true,
    validators: {
      async onSubmitAsync({ value }) {
        let parsed = createCompany.safeParse({
          name: value.name,
          type: value.type,
          storeName: value.storeName || undefined
        })
        if (!parsed.success) return parsed.error.issues
        await setup.mutateAsync(parsed.data)
      }
    }
  })

  let selectedType = useStore(form.store, (s) => s.values.type)
  let isSubmitting = useStore(form.store, (s) => s.isSubmitting)

  return (
    <SplashLayout>
      <form
        class={clsx($.form, $form.fullWidth, { [$anim.loadingOverlay]: isSubmitting })}
        style={{
          // fontSize: '1.25em'
          gap: '1rem'
        }}
        onSubmit={(e) => {
          e.preventDefault()
          form.handleSubmit()
        }}
      >
        <h3>Создание компании</h3>

        <form.Field name='name' validators={{ onChange: createCompany.shape.name }}>
          {(field) => (
            <div class={clsx($form.field, $form.fullWidth)}>
              <div class={$form.float}>
                <input
                  type='text'
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                  placeholder=' '
                  class={clsx({
                    [$form.error]: !field.state.meta.isValid && field.state.meta.isTouched
                  })}
                />
                <label>Название компании</label>
              </div>
              {field.state.meta.isTouched && <Errors errors={field.state.meta.errors} />}
            </div>
          )}
        </form.Field>

        <form.Field name='type'>
          {(field) => (
            <div class={$form.field}>
              <label>Профиль</label>
              <div class={$.typeSelector}>
                <button
                  type='button'
                  class={clsx($.typeOption, { [$.selected]: field.state.value === CompanyType.Distributor })}
                  onClick={() => field.handleChange(CompanyType.Distributor)}
                >
                  <TruckBoxFillIcon />
                  <span>Дистрибьютор</span>
                </button>
                <button
                  type='button'
                  class={clsx($.typeOption, { [$.selected]: field.state.value === CompanyType.Retailer })}
                  onClick={() => field.handleChange(CompanyType.Retailer)}
                >
                  <StorefrontFillIcon />
                  <span>Торговая компания</span>
                </button>
              </div>
              {field.state.meta.isTouched && <Errors errors={field.state.meta.errors} />}
            </div>
          )}
        </form.Field>

        {selectedType === CompanyType.Retailer && (
          <form.Field name='storeName' validators={{ onChange: createCompany.shape.storeName.unwrap() }}>
            {(field) => (
              <div class={clsx($form.field, $form.fullWidth)}>
                <div class={$form.float}>
                  <input
                    type='text'
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                    onBlur={field.handleBlur}
                    placeholder=' '
                    class={clsx({
                      [$form.error]: !field.state.meta.isValid && field.state.meta.isTouched
                    })}
                  />
                  <label>Название торговой точки</label>
                </div>
                <small class={$type.footnote}>Первая торговая точка вашей компании.</small>
                {field.state.meta.isTouched && <Errors errors={field.state.meta.errors} />}
              </div>
            )}
          </form.Field>
        )}

          <button type='submit' disabled={isSubmitting} class={$form.submit}>
            {isSubmitting && <Spinner />}
            Продолжить
          </button>
      </form>
    </SplashLayout>
  )
}
